import { useState, useEffect } from "react";
import { Stack, Spinner, SpinnerSize, DefaultButton, Text, Dropdown, IDropdownOption, PrimaryButton } from "@fluentui/react";
import { getSuStatistics, getSuCompanies, downloadSuStatistics } from "../../api/superuser/api_superuser";
import { SuStatisticsInfo, SuCompanyInfo, SuModelUsageInfo } from "../../api/superuser/models_superuser";
import { AdminLabel, ErrorDialog } from "../admin/components";

interface ConversationRow {
    key: string;
    company_cd: number;
    company_name: string;
    userid: string;
    conversation_id: string;
    tool_count: number;
    tools: string;
    templates: string;
    rags: string;
    isSummary?: boolean;
}

export const SuperuserStatistics = () => {
    const [statisticsData, setStatisticsData] = useState<SuStatisticsInfo[]>([]);
    const [modelUsageInfo, setModelUsageInfo] = useState<SuModelUsageInfo>();
    const [companies, setCompanies] = useState<SuCompanyInfo[]>([]);
    const [selectedCompanyCd, setSelectedCompanyCd] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const allCompanyKey = Number.MIN_SAFE_INTEGER

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            const companiesData = await getSuCompanies();
            if ("error" in companiesData) {
                setError(companiesData.error);
            } else {
                setCompanies(companiesData);
            }
        } catch (err) {
            setError("会社情報の取得中にエラーが発生しました");
        }
    };

    const fetchStatistics = async (year: number, month: number, companyCd: number | undefined) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getSuStatistics(year, month, companyCd);
            if ("error" in data) {
                setError(data.error);
            } else {
                setStatisticsData(data.statistics);
                setModelUsageInfo(data.model_usages)
                setCurrentYear(year);
                setCurrentMonth(month);
            }
        } catch (err) {
            setError("統計情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const result = await downloadSuStatistics(currentYear, currentMonth, selectedCompanyCd);
            if (result && "error" in result) {
                setError(result.error);
            }
        } catch (err) {
            setError("ダウンロード中にエラーが発生しました");
        } finally {
            setIsDownloading(false);
        }
    }

    const handlePrevMonth = () => {
        let newYear = currentYear;
        let newMonth = currentMonth - 1;
        if (newMonth === 0) {
            newYear -= 1;
            newMonth = 12;
        }
        fetchStatistics(newYear, newMonth, selectedCompanyCd);
    };

    const handleCurrentMonth = () => {
        const now = new Date();
        fetchStatistics(now.getFullYear(), now.getMonth() + 1, selectedCompanyCd);
    };

    const handleNextMonth = () => {
        let newYear = currentYear;
        let newMonth = currentMonth + 1;
        if (newMonth === 13) {
            newYear += 1;
            newMonth = 1;
        }
        fetchStatistics(newYear, newMonth, selectedCompanyCd);
    };

    const handleCompanyChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        const company_cd = Number(option?.key)
        setSelectedCompanyCd(company_cd);
        fetchStatistics(currentYear, currentMonth, company_cd === allCompanyKey ? undefined : company_cd);
    };

    const companyOptions: IDropdownOption[] = [
        { key: allCompanyKey, text: '全会社' },
        ...companies.map(company => ({
            key: company.company_cd,
            text: company.company_name
        }))
    ];

    const prepareAggregatedStats = () => {
        const toolUsageTotal: { [tool: string]: number } = {};
        const toolUsageUsers: { [tool: string]: Set<string> } = {};
        const templateUsageTotal: { [id: number]: number } = {};
        const templateUsageUsers: { [id: number]: Set<string> } = {};
        const ragUsageTotal: { [id: number]: number } = {};
        const ragUsageUsers: { [id: number]: Set<string> } = {};
        const modelUsageTotal: { [id: number]: number } = {};
        const modelUsageUsers: { [id: number]: Set<string> } = {};
        const templateNames: { [id: number]: string } = {};
        const ragNames: { [id: number]: string } = {};
        const modelNames: { [id: number]: string } = {};

        statisticsData.forEach(stat => {
            // ユーザーIDと会話IDのマッピング（逆引き）
            const convid_userid: { [convid: string]: string } = {};
            Object.entries(stat.userid_convids).forEach(([userid, convids]) => {
                convids.forEach(convid => {
                    convid_userid[convid] = userid;
                });
            });

            // convids_info から集計
            Object.entries(stat.convids_info).forEach(([convid, convInfo]) => {
                const userid = convid_userid[convid];

                // ツール集計
                Object.entries(convInfo.tool_usage || {}).forEach(([tool, count]) => {
                    toolUsageTotal[tool] = (toolUsageTotal[tool] || 0) + count;
                    if (!toolUsageUsers[tool]) {
                        toolUsageUsers[tool] = new Set();
                    }
                    if (userid) {
                        toolUsageUsers[tool].add(userid);
                    }
                });

                // テンプレート集計
                (convInfo.prmtmp_ids || []).forEach(id => {
                    templateUsageTotal[id] = (templateUsageTotal[id] || 0) + 1;
                    if (!templateNames[id]) {
                        templateNames[id] = stat.prmtmp_names[id] || `ID:${id}`;
                    }
                    if (!templateUsageUsers[id]) {
                        templateUsageUsers[id] = new Set();
                    }
                    if (userid) {
                        templateUsageUsers[id].add(userid);
                    }
                });

                // RAG集計
                (convInfo.rag_ids || []).forEach(id => {
                    ragUsageTotal[id] = (ragUsageTotal[id] || 0) + 1;
                    if (!ragNames[id]) {
                        ragNames[id] = stat.rag_names[id] || `ID:${id}`;
                    }
                    if (!ragUsageUsers[id]) {
                        ragUsageUsers[id] = new Set();
                    }
                    if (userid) {
                        ragUsageUsers[id].add(userid);
                    }
                });
            });
        });

        // モデル使用量の集計（modelUsageInfoから）
        if (modelUsageInfo) {
            modelUsageInfo.usages.forEach(usage => {
                if (usage.model_id !== null) {
                    const modelId = usage.model_id;
                    modelUsageTotal[modelId] = (modelUsageTotal[modelId] || 0) + 1;
                    
                    if (!modelNames[modelId]) {
                        modelNames[modelId] = modelUsageInfo.model_names[modelId] || `ID:${modelId}`;
                    }
                    
                    if (!modelUsageUsers[modelId]) {
                        modelUsageUsers[modelId] = new Set();
                    }
                    modelUsageUsers[modelId].add(usage.userid);
                }
            });
        }

        return {
            tools: Object.entries(toolUsageTotal)
                .map(([tool, count]) => ({ 
                    tool, 
                    count, 
                    userCount: toolUsageUsers[tool]?.size || 0 
                }))
                .sort((a, b) => b.count - a.count),
            templates: Object.entries(templateUsageTotal)
                .map(([id, count]) => ({ 
                    id: Number(id), 
                    name: templateNames[Number(id)], 
                    count,
                    userCount: templateUsageUsers[Number(id)]?.size || 0
                }))
                .sort((a, b) => b.count - a.count),
            rags: Object.entries(ragUsageTotal)
                .map(([id, count]) => ({ 
                    id: Number(id), 
                    name: ragNames[Number(id)], 
                    count,
                    userCount: ragUsageUsers[Number(id)]?.size || 0
                }))
                .sort((a, b) => b.count - a.count),
            models: Object.entries(modelUsageTotal)
                .map(([id, count]) => ({ 
                    id: Number(id), 
                    name: modelNames[Number(id)], 
                    count,
                    userCount: modelUsageUsers[Number(id)]?.size || 0
                }))
                .sort((a, b) => b.count - a.count)
        };
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    const aggregatedStats = prepareAggregatedStats();
    
    // statisticsDataから会話数を集計
    const totalConvNum = statisticsData.reduce((total, stat) => {
        return total + Object.keys(stat.convids_info).length;
    }, 0);

    const isInitialState = statisticsData.length === 0 && selectedCompanyCd === undefined
    const isSelectingAllCompany = statisticsData.length > 0 && selectedCompanyCd === undefined
    const currentSelectedCompanyName = isInitialState ? undefined : 
        (isSelectingAllCompany ? "全会社" : (companies.find((v) => v.company_cd == selectedCompanyCd)?.company_name ?? ""));

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="利用統計照会" />
            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                <Dropdown
                    placeholder="会社を選択"
                    label="会社選択"
                    options={companyOptions}
                    selectedKey={selectedCompanyCd}
                    onChange={handleCompanyChange}
                    styles={{ dropdown: { width: 300 } }}
                    disabled={isDownloading}
                />
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <DefaultButton text="前の月" onClick={handlePrevMonth} disabled={isDownloading} />
                    <DefaultButton text="今月" onClick={handleCurrentMonth} disabled={isDownloading} />
                    <DefaultButton text="次の月" onClick={handleNextMonth} disabled={isDownloading} />
                </Stack>
                <Stack.Item grow>
                    <Stack horizontal horizontalAlign="end">
                        <PrimaryButton text={`${currentSelectedCompanyName ?? "未選択"}_${currentYear}年${currentMonth}月利用統計`} onClick={handleDownload} iconProps={{ iconName: 'Download' }} disabled={isDownloading || isInitialState} />
                    </Stack>
                </Stack.Item>
            </Stack>

            <Text variant="xLarge" block>
                {currentYear}年{currentMonth}月分 利用統計 {currentSelectedCompanyName ? `＜${currentSelectedCompanyName}＞` : ""}
            </Text>

            {/* 使用回数内訳 */}
            <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                {/* テンプレート使用内訳 */}
                <Stack 
                    tokens={{ childrenGap: 10 }} 
                    styles={{ 
                        root: { 
                            padding: 15, 
                            border: '1px solid #e1e1e1', 
                            borderRadius: 4,
                            minWidth: 250,
                            flex: 1
                        } 
                    }}
                >
                    <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                        {`テンプレート使用会話数 (${aggregatedStats.templates.reduce((acc, v) => acc+v.count, 0)}/${totalConvNum})`}
                    </Text>
                    {aggregatedStats.templates.length > 0 ? (
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack horizontal styles={{ root: { display: 'grid', gridTemplateColumns: '40px 1fr 60px 50px', gap: 8, alignItems: 'center', paddingBottom: 4, borderBottom: '1px solid #e1e1e1' } }}>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>ID</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>名前</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>会話数</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>人数</Text>
                            </Stack>
                            {aggregatedStats.templates.map(({ id, name, count, userCount }) => (
                                <Stack horizontal key={id} styles={{ root: { display: 'grid', gridTemplateColumns: '40px 1fr 60px 50px', gap: 8, alignItems: 'center' } }}>
                                    <Text variant="small" styles={{ root: { textAlign: 'right' } }}>{id}</Text>
                                    <Text variant="small" styles={{ root: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>{name}</Text>
                                    <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>{count}個</Text>
                                    <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>{userCount}人</Text>
                                </Stack>
                            ))}
                        </Stack>
                    ) : (
                        <Text variant="small" styles={{ root: { color: '#666' } }}>使用なし</Text>
                    )}
                </Stack>

                {/* RAG使用内訳 */}
                <Stack 
                    tokens={{ childrenGap: 10 }} 
                    styles={{ 
                        root: { 
                            padding: 15, 
                            border: '1px solid #e1e1e1', 
                            borderRadius: 4,
                            minWidth: 250,
                            flex: 1
                        } 
                    }}
                >
                    <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                        {`RAG使用会話数 (${aggregatedStats.rags.reduce((acc, v) => acc+v.count, 0)}/${totalConvNum})`}
                    </Text>
                    {aggregatedStats.rags.length > 0 ? (
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack horizontal styles={{ root: { display: 'grid', gridTemplateColumns: '40px 1fr 60px 50px', gap: 8, alignItems: 'center', paddingBottom: 4, borderBottom: '1px solid #e1e1e1' } }}>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>ID</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>名前</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>会話数</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>人数</Text>
                            </Stack>
                            {aggregatedStats.rags.map(({ id, name, count, userCount }) => (
                                <Stack horizontal key={id} styles={{ root: { display: 'grid', gridTemplateColumns: '40px 1fr 60px 50px', gap: 8, alignItems: 'center' } }}>
                                    <Text variant="small" styles={{ root: { textAlign: 'right' } }}>{id}</Text>
                                    <Text variant="small" styles={{ root: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>{name}</Text>
                                    <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>{count}個</Text>
                                    <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>{userCount}人</Text>
                                </Stack>
                            ))}
                        </Stack>
                    ) : (
                        <Text variant="small" styles={{ root: { color: '#666' } }}>使用なし</Text>
                    )}
                </Stack>

                {/* モデル使用内訳 */}
                <Stack 
                    tokens={{ childrenGap: 10 }} 
                    styles={{ 
                        root: { 
                            padding: 15, 
                            border: '1px solid #e1e1e1', 
                            borderRadius: 4,
                            minWidth: 250,
                            flex: 1
                        } 
                    }}
                >
                    <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                        モデル使用回数
                    </Text>
                    {aggregatedStats.models.length > 0 ? (
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack horizontal styles={{ root: { display: 'grid', gridTemplateColumns: '40px 1fr 60px 50px', gap: 8, alignItems: 'center', paddingBottom: 4, borderBottom: '1px solid #e1e1e1' } }}>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>ID</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>名前</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>回数</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>人数</Text>
                            </Stack>
                            {aggregatedStats.models.map(({ id, name, count, userCount }) => (
                                <Stack horizontal key={id} styles={{ root: { display: 'grid', gridTemplateColumns: '40px 1fr 60px 50px', gap: 8, alignItems: 'center' } }}>
                                    <Text variant="small" styles={{ root: { textAlign: 'right' } }}>{id}</Text>
                                    <Text variant="small" styles={{ root: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>{name}</Text>
                                    <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>{count}回</Text>
                                    <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>{userCount}人</Text>
                                </Stack>
                            ))}
                        </Stack>
                    ) : (
                        <Text variant="small" styles={{ root: { color: '#666' } }}>使用なし</Text>
                    )}
                </Stack>

                {/* ツール使用内訳 */}
                <Stack 
                    tokens={{ childrenGap: 10 }} 
                    styles={{ 
                        root: { 
                            padding: 15, 
                            border: '1px solid #e1e1e1', 
                            borderRadius: 4,
                            minWidth: 250,
                            flex: 1
                        } 
                    }}
                >
                    <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                        ツール使用回数
                    </Text>
                    {aggregatedStats.tools.length > 0 ? (
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack horizontal styles={{ root: { display: 'grid', gridTemplateColumns: '1fr 60px 50px', gap: 8, alignItems: 'center', paddingBottom: 4, borderBottom: '1px solid #e1e1e1' } }}>
                                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>名前</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>回数</Text>
                                <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>人数</Text>
                            </Stack>
                            {aggregatedStats.tools.map(({ tool, count, userCount }) => (
                                <Stack horizontal key={tool} styles={{ root: { display: 'grid', gridTemplateColumns: '1fr 60px 50px', gap: 8, alignItems: 'center' } }}>
                                    <Text variant="small" styles={{ root: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>{tool}</Text>
                                    <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>{count}回</Text>
                                    <Text variant="small" styles={{ root: { fontWeight: 600, textAlign: 'right' } }}>{userCount}人</Text>
                                </Stack>
                            ))}
                        </Stack>
                    ) : (
                        <Text variant="small" styles={{ root: { color: '#666' } }}>使用なし</Text>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
}
